// Angular
import {
  ChangeDetectorRef,
  ElementRef,
  Injectable
} from "@angular/core";

// Material
import { MatDialog } from "@angular/material/dialog";

// Local library
import { RunnerService } from "./runner.service";
import { SessionService } from "@amwager/data-access";
import {
  GeoLocationWebService,
  MatNotificationService,
  UnsubscribeObservableService
} from "@amwager/util";
import { ticketRequest } from "@amwager/util-ts";
import {
  HorseLoaderService,
  WagerConfirmDialogComponent,
  WagerConfirmDialogData
} from "@amwager/feature";
import {
  IBetTicket,
  WagerFacade
} from "../+state";

// Third party
import _ from "lodash";
import {
  firstValueFrom,
  Subscription,
  takeUntil
} from "rxjs";

@Injectable()
export class BetService extends UnsubscribeObservableService {

  betAmounts: number[] = [0.1, 0.20, 0.5, 1, 2, 3, 4, 5, 10, 20, 50, 100, 200, 500, 1000];
  showBetAmounts: number[] = [];
  minBet!: number;
  maxBet!: number;

  modifierEnam: string[] = [
    'Straight',
    'Wheel',
    'Box',
    'PowerBox',
    'Leading',
    'KeyWheel',
    'KeyBox',
    'DutchCost',
    'DutchPayout',
    'ABC'
  ];

  private betTicketPayload: IBetTicket = {
    CustomerId: '',
    DutchType: 0,
    FileUploadName: null,
    // Location: {
    //   CLat: 0,
    //   CLon: 0
    // },
    NickName: null,
    RunId: '',
    TicketAmount: 0,
    ToteTicketRequest: {
      CurrencyId: 'USD',
      EventId: '',
      ParentStackId: "",
      RaceId: 0,
      StackId: "",
      Wagers: [{
        // RaceId: 0,
        BaseAmount: 0,
        // BaseAmount: '',
        Bets: 0,
        Legs: [{
          Auto: false,
          Runners: ""
        }],
        Modifier: 0,
        PoolId: "",
        Total: 0
      }]
    }
  };

  isExceedsSaveBetErr: boolean = false;
  isExceedsBetNowErr: boolean = false;
  isGenericSaveBetErr: boolean = false;
  isGenericBetNowErr: boolean = false;

  private saveBetResponseSubs?: Subscription;
  private betNowResponseSubs?: Subscription;

  constructor(
    private readonly runnerService: RunnerService,
    private readonly sessionService: SessionService,
    private readonly matNotificationService: MatNotificationService,
    private readonly geoLocationWebService: GeoLocationWebService,
    private readonly wagerFacade: WagerFacade,
    private readonly dialog: MatDialog,
    private readonly horseLoaderService: HorseLoaderService,
  ) {
    super();
  }

  /**
   * True when the payload has at least one active runner selection
   * @param {any} payload Show-bet payload from runner.component
   * @returns {boolean} True when a wager is being built
   */
  private hasAnyRunnerSelection(payload: any): boolean {
    if (payload?.storedSelectedRunners) return true;
    const storeStructure = payload?.storeStructure;
    if (!Array.isArray(storeStructure) || storeStructure.length === 0)
      return false;
    return _.some(
      storeStructure,
      (leg: any) =>
        Array.isArray(leg?.runners) && leg.runners.length > 0
    );
  }

  /**
   * Convert modifier name to two-letter abbreviation
   * @param {string} word Modifier name from the wager UI
   * @returns {string} Abbreviated label, or ''
   */
  private convertWordsToAbbre(word: string): string {
    switch (word) {
      case 'Box':
      case 'BX':
        return 'BX';
      case 'KeyBox':
      case 'KB':
        return 'KB';
      case 'PowerBox':
      case 'PB':
        return 'PB';
      case 'KeyWheel':
      case 'KW':
        return 'KW';
      default:
        return '';
    }
  }

  /**
   * Collapse 3+ consecutive runner numbers in each leg into a range
   * @param {string} runners Slash-separated, comma-delimited runner numbers
   * @returns {string} Same string with consecutive runs replaced by ranges
   */
  private formatConsecutiveRunners(runners: string): string {
    if (!runners) return '';

    const numbersGroups: string[] = runners.split('/');
    const formattedGroups: string[] = [];

    for (const group of numbersGroups) {
      const numbers: string[] = group.split(',');
      let consecutive: string[] = [];
      const formattedGroup: string[] = [];

      for (let i = 0; i < numbers.length; i++) {
        if (parseInt(numbers[i], 10) + 1 === parseInt(numbers[i + 1], 10)) {
          consecutive.push(numbers[i]);
        } else {
          consecutive.push(numbers[i]);

          if (consecutive.length >= 3) {
            formattedGroup.push(
              `${consecutive[0]}-${consecutive[consecutive.length - 1]}`
            );
          } else {
            formattedGroup.push(...consecutive);
          }

          consecutive = [];
        }
      }

      formattedGroups.push(formattedGroup.join(','));
    }

    return formattedGroups.join('/');
  }

  /**
   * Open the confirm dialog when required, else place the bet directly
   * @param {string} betType 'bet-now' or 'save-bet'
   * @param {any} importData Bet preview payload
   * @param {any} selectedEvent Selected event
   * @param {any} selectedRace Selected race
   * @param {any} selectedPool Selected pool
   * @param {string} selectedModifier Selected modifier
   * @param {number} selectedAmount Selected base amount
   * @param {string} storedSelectedRunners Formatted runner numbers
   * @param {boolean} requireConfirmation Show confirm dialog first
   * @param {any} geoLocation Cached geo location
   * @param {ElementRef} elementRef Optional ref for clearing UI state
   * @param {boolean} isClearWager Clear the wager on success
   * @param {() => void} onCancel Callback fired when the dialog is dismissed
   * @returns {Promise<void>}
   */
  async isRequireConfirmActive(
    betType: string,
    importData: any,
    selectedEvent: any,
    selectedRace: any,
    selectedPool: any,
    selectedModifier: string,
    selectedAmount: number,
    storedSelectedRunners: string,
    requireConfirmation: boolean,
    geoLocation: any,
    elementRef?: ElementRef,
    isClearWager?: boolean,
    onCancel?: () => void
  ): Promise<void> {
    if (requireConfirmation) {

      const eventName = selectedEvent?.eventName
        ?? selectedEvent?.ToteResponse?.EventDetail?.EventName
        ?? selectedEvent?.ToteResponse?.EventDetail?.EventId
        ?? '';
      const raceId = selectedRace !== undefined ? selectedRace.RaceId : '';
      const poolId = selectedPool !== undefined ? selectedPool.PoolId : '';
      const modifierAbbr = this.convertWordsToAbbre(selectedModifier);
      const rawRunners =
        storedSelectedRunners || importData?.storedSelectedRunners || '';
      const formattedRunners = this.formatConsecutiveRunners(rawRunners);

      const importBets = importData?.bets ?? 1;
      const totalAmount = (selectedAmount || 0) * (importBets || 1);

      const dialogData: WagerConfirmDialogData = {
        betType,
        title: betType === 'bet-now' ? 'Place Wager' : 'Save Wager',
        eventName,
        raceId,
        baseAmount: selectedAmount,
        poolId,
        modifier: selectedModifier,
        modifierAbbr,
        runners: formattedRunners,
        totalAmount: totalAmount > 0 ? totalAmount : undefined,
      };

      const dialogRef = this.dialog.open(WagerConfirmDialogComponent, {
        data: dialogData,
        width: '340px',
        maxWidth: '95vw',
        autoFocus: false,
        disableClose: true,
        enterAnimationDuration: '120ms',
        exitAnimationDuration: '0ms',
        panelClass: 'wager-confirm-modal-dialog',
      });

      dialogRef.afterClosed()
        .pipe(takeUntil(this.toUnsubscribe$))
        .subscribe((confirmed: boolean | undefined) => {
          if (!confirmed) {
            onCancel?.();
            return;
          }

          this.runBetNow(
            betType,
            importData,
            selectedEvent,
            selectedRace,
            selectedPool,
            selectedModifier,
            selectedAmount,
            geoLocation,
            elementRef,
            isClearWager
          );
        });

      return;
    }

    this.runBetNow(
      betType,
      importData,
      selectedEvent,
      selectedRace,
      selectedPool,
      selectedModifier,
      selectedAmount,
      geoLocation,
      elementRef,
      isClearWager
    );
  }

  /**
   * Dispatch betNow with the right dutch / abc / straight payload
   * @param {string} betType 'bet-now' or 'save-bet'
   * @param {any} importData Bet preview payload
   * @param {any} selectedEvent Selected event
   * @param {any} selectedRace Selected race
   * @param {any} selectedPool Selected pool
   * @param {string} selectedModifier Selected modifier
   * @param {number} selectedAmount Selected base amount
   * @param {any} geoLocation Cached geo location
   * @param {ElementRef} elementRef Optional ref for clearing UI state on success
   * @param {boolean} isClearWager Whether to clear the wager on success
   * @returns {void}
   */
  private runBetNow(
    betType: string,
    importData: any,
    selectedEvent: any,
    selectedRace: any,
    selectedPool: any,
    selectedModifier: string,
    selectedAmount: number,
    geoLocation: any,
    elementRef?: ElementRef,
    isClearWager?: boolean
  ): void {
    const modifierLc = selectedModifier?.toLowerCase();

    if (modifierLc === 'dutchcost') {
      this.betNow(
        betType,
        {
          betType,
          typeIs: 'dutch',
          dutchTicket: importData?.dutchTicket
        },
        elementRef,
        isClearWager,
        importData,
        selectedEvent,
        selectedRace,
        selectedPool,
        selectedModifier,
        selectedAmount,
        geoLocation
      );
      return;
    }

    if (modifierLc === 'abc') {
      this.betNow(
        betType,
        {
          betType,
          typeIs: 'abc',
          abcTicket: this.createNewABCTicket(importData?.abcTicket, true)
        },
        elementRef,
        isClearWager,
        importData,
        selectedEvent,
        selectedRace,
        selectedPool,
        selectedModifier,
        selectedAmount,
        geoLocation
      );
      return;
    }

    this.betNow(
      betType,
      undefined,
      elementRef,
      isClearWager,
      importData,
      selectedEvent,
      selectedRace,
      selectedPool,
      selectedModifier,
      selectedAmount,
      geoLocation
    );
  }

  /**
   * Build the bet ticket payload and dispatch to save or submit
   * @param {string} betType 'bet-now' or 'save-bet'
   * @param {any} payload Optional dutch/abc payload
   * @param {ElementRef} elementRef Ref for clearing UI state
   * @param {boolean} isClearWager Clear the wager on success
   * @param {any} importData Bet preview payload
   * @param {any} selectedEvent Selected event
   * @param {any} selectedRace Selected race
   * @param {any} selectedPool Selected pool
   * @param {string} selectedModifier Selected modifier
   * @param {number} selectedAmount Selected base amount
   * @param {any} geoLocation Cached geo location
   * @returns {Promise<void>}
   */
  async betNow(
    betType: string,
    payload?: any,
    elementRef?: ElementRef,
    isClearWager?: boolean,
    importData?: any,
    selectedEvent?: any,
    selectedRace?: any,
    selectedPool?: any,
    selectedModifier?: string,
    selectedAmount?: number,
    geoLocation?: any,
  ): Promise<void> {

    if (importData?.cantBet) {
      this.matNotificationService.showAlert(
        'Error',
        'Failed to validate ticket. Race not Open.',
        '',
        '',
        'Close'
      );
      return;
    }

    let betTicket: IBetTicket = {};

    if (betType === 'save-bet') {
      betTicket = {
        ...this.betTicketPayload,
        TicketAmount: (selectedAmount ?? 0) * (importData.bets ?? 0),
        RunId: selectedEvent.ToteResponse.RunId,
        ToteTicketRequest: {
          ...this.betTicketPayload?.ToteTicketRequest,
          EventId: selectedEvent.ToteResponse.EventDetail.EventId,
          RaceId: selectedRace.RaceId,
          Wagers: [
            {
              ...this.betTicketPayload?.ToteTicketRequest?.Wagers?.[
                0
              ],
              PoolId: selectedPool.PoolId,
              Bets: importData.bets,
              Legs: importData.legs,
              Modifier: this.runnerService.getModId(selectedModifier ?? ''),
              BaseAmount: selectedAmount,
              Total: this.runnerService.betTotal(importData?.bets ?? 0, selectedAmount ?? 0)
            }
          ]
        }
      }

      if (payload) {
        if (payload?.typeIs === 'dutch' && betTicket?.ToteTicketRequest !== undefined) {

          const extractWagerData = _.map(
            _.filter(payload?.dutchTicket.Wagers, (item) => !item.Error),
            (item) => _.pick(item, ['PoolId', 'BaseAmount', 'Modifier', 'Legs', 'Bets', 'Total'])
          );

          const checkWagerModifier = _.forEach(extractWagerData, wager => {
            wager.Modifier = (
              this.modifierEnam.indexOf(wager.Modifier) < 0 ||
              this.modifierEnam.indexOf(wager.Modifier) > 6
               ?
              0
               :
              this.modifierEnam.indexOf(wager.Modifier)
            )
          });

          const cleanWagerData = _.forEach(checkWagerModifier, wager => {
            _.forEach(wager.Legs, legItem => {
              if (_.isArray(legItem.Runners)) {
                legItem.Runners = legItem.Runners.join(",");
              }
            });
          });

          betTicket.TicketAmount = importData.dutchTicket.calcAmount();
          betTicket.ToteTicketRequest.Wagers = cleanWagerData;
          betTicket.DutchType = payload?.dutchTicket.DutchType;
          betTicket.DutchProjections = payload?.dutchTicket.DutchProjections;

          betTicket = {
            ...betTicket,
            ToteTicketRequest: betTicket.ToteTicketRequest
              && (
                'ParentStackId' in betTicket.ToteTicketRequest ||
                'StackId' in betTicket.ToteTicketRequest
              )
              ? _.omit(betTicket.ToteTicketRequest, ['ParentStackId', 'StackId'])
              : betTicket.ToteTicketRequest
          };
        }

        if (payload?.typeIs === 'abc' && betTicket?.ToteTicketRequest !== undefined) {

          const extractWagerData = _.map(
            _.filter(payload?.abcTicket.Wagers, (item) => !item.Error),
            (item) => _.pick(item, ['PoolId', 'BaseAmount', 'Modifier', 'Legs', 'Bets', 'Total'])
          );

          const checkWagerModifier = _.forEach(extractWagerData, wager => {
            wager.Modifier = (
              this.modifierEnam.indexOf(wager.Modifier) < 0 ||
              this.modifierEnam.indexOf(wager.Modifier) > 6
               ?
              0
               :
              this.modifierEnam.indexOf(wager.Modifier)
            )
          });

          const cleanWagerData = _.forEach(checkWagerModifier, wager => {
            _.forEach(wager.Legs, legItem => {
              if (_.isArray(legItem.Runners)) {
                legItem.Runners = legItem.Runners.join(",");
              }
            });
          });

          betTicket.TicketAmount = 0;
          betTicket.ToteTicketRequest.Wagers = cleanWagerData;
          betTicket.DutchType = payload?.abcTicket.DutchType;
        }
      }

    }
    else {
      betTicket = {
        ...this.betTicketPayload,
        TicketAmount: (selectedAmount ?? 0) * (importData?.bets ?? 0),
        RunId: selectedEvent.ToteResponse.RunId,
        ToteTicketRequest: {
          ...this.betTicketPayload?.ToteTicketRequest,
          EventId: selectedEvent.ToteResponse.EventDetail.EventId,
          RaceId: selectedRace.RaceId,
          Wagers: [
            {
              ...this.betTicketPayload?.ToteTicketRequest?.Wagers?.[
                0
              ],
              PoolId: selectedPool.PoolId,
              Bets: importData.bets,
              Legs: importData.legs,
              Modifier: this.runnerService.getModId(selectedModifier ?? ''),
              BaseAmount: selectedAmount,
              Total: this.runnerService.betTotal(importData?.bets ?? 0, selectedAmount ?? 0)
            }
          ]
        }
      }

      if (payload) {
        if (payload?.typeIs === 'dutch' && betTicket?.ToteTicketRequest !== undefined) {

          const extractWagerData = _.map(
            _.filter(payload?.dutchTicket.Wagers, (item) => !item.Error),
            (item) => _.pick(item, ['PoolId', 'BaseAmount', 'Modifier', 'Legs', 'Bets', 'Total'])
          );

          const checkWagerModifier = _.forEach(extractWagerData, wager => {
            wager.Modifier = (
              this.modifierEnam.indexOf(wager.Modifier) < 0 ||
              this.modifierEnam.indexOf(wager.Modifier) > 6
               ?
              0
               :
              this.modifierEnam.indexOf(wager.Modifier)
            )
          });

          const cleanWagerData = _.forEach(checkWagerModifier, wager => {
            _.forEach(wager.Legs, legItem => {
              if (_.isArray(legItem.Runners)) {
                legItem.Runners = legItem.Runners.join(",");
              }
            });
          });

          betTicket.TicketAmount = importData.dutchTicket.calcAmount();
          betTicket.ToteTicketRequest.Wagers = cleanWagerData;
          betTicket.DutchType = payload?.dutchTicket.DutchType;
          betTicket.DutchProjections = payload?.dutchTicket.DutchProjections;

          betTicket = {
            ...betTicket,
            ToteTicketRequest: betTicket.ToteTicketRequest
              && (
                'ParentStackId' in betTicket.ToteTicketRequest ||
                'StackId' in betTicket.ToteTicketRequest
              )
              ? _.omit(betTicket.ToteTicketRequest, ['ParentStackId', 'StackId'])
              : betTicket.ToteTicketRequest
          };

        }

        if (payload?.typeIs === 'abc' && betTicket?.ToteTicketRequest !== undefined) {

          const extractWagerData = _.map(
            _.filter(payload?.abcTicket.Wagers, (item) => !item.Error),
            (item) => _.pick(item, ['PoolId', 'BaseAmount', 'Modifier', 'Legs', 'Bets', 'Total'])
          );

          betTicket.TicketAmount = _.sumBy(extractWagerData, 'Total');

          const checkWagerModifier = _.forEach(extractWagerData, wager => {
            wager.Modifier = (
              this.modifierEnam.indexOf(wager.Modifier) < 0 ||
              this.modifierEnam.indexOf(wager.Modifier) > 6
               ?
              0
               :
              this.modifierEnam.indexOf(wager.Modifier)
            )
          });

          const cleanWagerData = _.forEach(checkWagerModifier, wager => {
            _.forEach(wager.Legs, legItem => {
              if (_.isArray(legItem.Runners)) {
                legItem.Runners = legItem.Runners.join(",");
              }
            });
          });

          betTicket.ToteTicketRequest.Wagers = cleanWagerData;
          betTicket.DutchType = payload?.abcTicket.DutchType;
        }
      }

    }

    if (this.sessionService.getSubscription()) {
      betTicket.CustomerId = this.sessionService.getSubscription();
    }

    // if (geoLocation === null) {
    //   await this.geoLocationWebService.askPermissionGeoLocation();
    //   const result = await firstValueFrom(this.geoLocationWebService.geo$);

    //   if (result !== null && 'latitude' in result && 'longitude' in result) {
    //     if (result === undefined) return;

    //     if (
    //       (result?.latitude === 0) || (result?.longitude === 0)
    //       || ((result?.latitude as any) === '0') || ((result?.longitude as any) === '0')
    //       || (result?.latitude === 0.0) || (result?.longitude === 0.0)
    //       || ((result?.latitude as any) === '0.0') || ((result?.longitude as any) === '0.0')
    //       || !result?.latitude || !result?.longitude
    //     ) return;

    //     if (result.latitude && result.longitude) {
    //       this.wagerFacade.setGeoLocation({
    //         CLat: result.latitude, CLon: result.longitude
    //       },
    //       'BetPreviewComponent:betNow');

    //         betTicket = {
    //           ...betTicket,
    //           Location: {
    //             ...betTicket?.Location,
    //             CLat: result.latitude,
    //             CLon: result.longitude
    //           }
    //         };
    //     }
    //   }
    // }
    // else {
      const latitude = geoLocation?.CLat || undefined;
      const longitude = geoLocation?.CLon || undefined;

    //   if (
    //     (latitude === 0) || (longitude === 0)
    //     || ((latitude as any) === '0') || ((longitude as any) === '0')
    //     || (latitude === 0.0) || (longitude === 0.0)
    //     || ((latitude as any) === '0.0') || ((longitude as any) === '0.0')
    //     || !latitude || !longitude
    //   ) return;

    //   if (latitude && longitude) {
          betTicket = {
            ...betTicket,
            Location: {
              ...betTicket?.Location,
              CLat: latitude,
              CLon: longitude
            }
          };
    //   }
    // }

    if (betType === 'save-bet')
      this.execSaveBet(betTicket, elementRef, isClearWager ?? undefined);
    else
      this.execBetNow(betTicket, elementRef, isClearWager ?? undefined);
  }

  /**
   * Dispatch save-bet API and handle the success/error response
   * @param {IBetTicket} betTicket Bet ticket payload
   * @param {ElementRef} elementRef Ref for clearing UI state
   * @param {boolean} isClearWager Clear the wager on success
   * @returns {void}
   */
  execSaveBet(betTicket: IBetTicket, elementRef?: ElementRef, isClearWager?: boolean): void {

    if (betTicket?.ToteTicketRequest?.Wagers) {
      if (betTicket?.ToteTicketRequest?.Wagers?.length < 1) {
        this.matNotificationService.showAlert(
          'Error',
          'No valid ticket to Save',
          '',
          '',
          'Close',
          '',
          false
        );
        // this.handleWagerAlertService.triggerConfirmMessage(null);
        this.clearProcessingWager();
        return;
      }
    }

    if (this.isExceedsSaveBetErr)
      this.isExceedsSaveBetErr = false;

    if (this.isGenericSaveBetErr)
      this.isGenericSaveBetErr = false;

    this.horseLoaderService.show();

    this.wagerFacade.saveBetAPI(betTicket);

    this.saveBetResponseSubs?.unsubscribe();
    this.saveBetResponseSubs = this.wagerFacade.isSaveBetSuccess$()
    .pipe(takeUntil(this.toUnsubscribe$))
    .subscribe( async(callback) => {
      if (callback?.isSuccess === undefined) {
        return;
      }

      const errorMessage = callback.response?.WageringResponses?.[0]?.Response?.Error?.Message ?
        callback.response?.WageringResponses?.[0]?.Response?.Error?.Message
      :
        callback.response?.WageringResponses?.[0]?.Response?.Wagers?.[0]?.Error?.Message;

      this.isExceedsSaveBetErr = _.some(
        callback.response?.WageringResponses,
        {'Error': 'Exceeds loss limit.'}
      );

      if (this.isExceedsSaveBetErr) {
        this.matNotificationService.showAlert(
          'Error',
          'Exceeds loss limit.',
          '',
          '',
          'Close',
          '',
          true
        );
      }

      this.isGenericSaveBetErr = _.some(callback.response?.WageringResponses, (item) =>
        item?.Error?.includes('Please contact your administrator for more information.')
      );

      if (
        callback.response?.Error?.includes('General exception while trying to stack single ticket')
        || callback.response?.Error?.includes('Error')
      )
        this.isGenericSaveBetErr = true;

      if (this.isGenericSaveBetErr) {
        this.matNotificationService.showAlert(
          'Error',
          'Invalid ticket or wager. Please adjust selections.',
          '',
          '',
          'Close',
          '',
          true
        );
      }

      if (errorMessage) {
        this.matNotificationService.showAlert(
          'Error',
          errorMessage,
          '',
          '',
          'Close',
          '',
          true
        );
      }

      // API succeeded only when flag is true AND no error channels fired.
      const isApiSuccess =
        callback?.isSuccess === true &&
        !errorMessage &&
        !this.isExceedsSaveBetErr &&
        !this.isGenericSaveBetErr;

      if (isClearWager && isApiSuccess) {
        this.runnerService.triggerClearRunnerWagerState();
        this.runnerService.clearActiveAllLeg(elementRef ?? new ElementRef(null));
        this.runnerService.clearLegRoundSelector(elementRef ?? new ElementRef(null));
      }

      this.clearProcessingWager();
      this.horseLoaderService.hide();

      this.saveBetResponseSubs?.unsubscribe();
    });
  }

  /**
   * Dispatch bet-now API and handle the success/error response
   * @param {IBetTicket} betTicket Bet ticket payload
   * @param {ElementRef} elementRef Ref for clearing UI state
   * @param {boolean} isClearWager Clear the wager on success
   * @returns {void}
   */
  execBetNow(betTicket: IBetTicket, elementRef?: ElementRef, isClearWager?: boolean): void {

    if (betTicket?.ToteTicketRequest?.Wagers) {
      if (betTicket?.ToteTicketRequest?.Wagers?.length < 1) {
        this.matNotificationService.showAlert(
          'Error',
          'No valid ticket to Submit',
          '',
          '',
          'Close',
          '',
          true
        );
        // this.handleWagerAlertService.triggerConfirmMessage(null);
        this.clearProcessingWager();
        return;
      }
    }

    if (this.isExceedsBetNowErr)
      this.isExceedsBetNowErr = false;

    if (this.isGenericBetNowErr)
      this.isGenericBetNowErr = false;

    this.horseLoaderService.show();

    this.wagerFacade.betNowAPI(betTicket);

    this.betNowResponseSubs?.unsubscribe();
    this.betNowResponseSubs = this.wagerFacade.isBetNowSuccess$()
    .pipe(takeUntil(this.toUnsubscribe$))
    .subscribe( async(callback) => {
      if (callback?.response?.error) {
        if (callback?.response?.error?.MessageCode?.includes?.('geolocation.clatclon.null')) {
          this.showAlertGeoLocation(
            callback?.response?.error?.Message ||
            'We are unable to verify your location. Please enable location services and try again.'
          );
          this.clearProcessingWager();
          this.horseLoaderService.hide();
          this.betNowResponseSubs?.unsubscribe();
          return;
        }
      }

      if (callback?.isSuccess === undefined) {
        return;
      }

      const errorMessage = callback.response?.WageringResponses?.[0]?.Response?.Error?.Message ?
        callback.response?.WageringResponses?.[0]?.Response?.Error?.Message
      :
        callback.response?.WageringResponses?.[0]?.Response?.Wagers?.[0]?.Error?.Message;

      this.isExceedsBetNowErr = _.some(
        callback.response?.WageringResponses,
        {'Error': 'Exceeds loss limit.'}
      );

      if (this.isExceedsBetNowErr) {
        this.matNotificationService.showAlert(
          'Error',
          'Exceeds loss limit.',
          '',
          '',
          'Close',
          '',
          true
        );
      }

      this.isGenericBetNowErr = _.some(callback.response?.WageringResponses, (item) =>
        item?.Error?.includes('Please contact your administrator for more information.')
      );

      if (
        callback.response?.error?.Model?.Error?.includes(
          'General exception while trying to issue single ticket'
        )
        || callback.response?.error?.Model?.Error?.includes('Error')
      )
        this.isGenericBetNowErr = true;

      if (this.isGenericBetNowErr) {
        this.matNotificationService.showAlert(
          'Error',
          'Invalid ticket or wager. Please adjust selections.',
          '',
          '',
          'Close',
          '',
          true
        );
      }

      if (errorMessage) {
        this.matNotificationService.showAlert(
          'Error',
          errorMessage,
          '',
          '',
          'Close',
          '',
          true
        );
      }

      // API succeeded only when flag is true AND no error channels fired.
      const isApiSuccess =
        callback?.isSuccess === true &&
        !errorMessage &&
        !this.isExceedsBetNowErr &&
        !this.isGenericBetNowErr;

      if (isClearWager && isApiSuccess) {
        this.runnerService.triggerClearRunnerWagerState();
        this.runnerService.clearActiveAllLeg(elementRef ?? new ElementRef(null));
        this.runnerService.clearLegRoundSelector(elementRef ?? new ElementRef(null));
      }

      this.clearProcessingWager();
      this.horseLoaderService.hide();

      this.betNowResponseSubs?.unsubscribe();
    });
  }

  /**
   * Show geo-location alert with "Get new location" / "Close" buttons
   * @param {string} error Error text returned by the wagering API
   * @returns {void}
   */
  showAlertGeoLocation(error: string): void {
    const confirm$ = this.matNotificationService.showAlert(
      'Error',
      error,
      '',
      'Get new location',
      'Close',
      '',
      false,
    );

    confirm$.pipe(takeUntil(this.toUnsubscribe$)).subscribe(async (confirmed: boolean) => {
      if (!confirmed) return;

      await this.geoLocationWebService.askPermissionGeoLocation();
      const result = await firstValueFrom(this.geoLocationWebService.geo$);

      if (
        result &&
        'latitude' in result &&
        'longitude' in result &&
        result.latitude &&
        result.longitude
      ) {
        this.wagerFacade.setGeoLocation(
          { CLat: result.latitude, CLon: result.longitude },
          'BetService:showAlertGeoLocation'
        );
      }
    });
  }

  /**
   * Build a new ABC ticket from unsubmitted wagers
   * @param {any} abcTicket Source ABC ticket list
   * @param {boolean} isIndividual True for individual, false for combo
   * @returns {any} New ticket request
   */
  createNewABCTicket(abcTicket: any, isIndividual: boolean): any {
    const wagersList = [];
    for(let i=0;i<abcTicket.length;i++) {
      if(
        (abcTicket[i].submitted === undefined || abcTicket[i].submitted === false)
        &&
        (abcTicket[i].saved === undefined || abcTicket[i].saved === false)
        &&
        (abcTicket[i].removed === undefined || abcTicket[i].removed === false)
      ) {
        for(let j = 0; j < abcTicket[i].ABCTicket.Wagers.length; j++) {
          wagersList.push(abcTicket[i].ABCTicket.Wagers[j]);
        }
      }
    }

    return new ticketRequest(
      abcTicket[0].ABCTicket.RunId,
      abcTicket[0].ABCTicket.EventId,
      abcTicket[0].ABCTicket.RaceId,
      wagersList,
      isIndividual ? 0 : 3,
      undefined
    );
  }

  /**
   * Build the list of bet-preview error lines (dutch/abc/cant-bet/balance)
   * @param {any} payload Show-bet payload from runner.component
   * @param {number} balanceAmount Account balance
   * @param {number} maxWagerTotal Max wager total cap
   * @returns {string[]} Applicable error lines, or []
   */
  toDisplayBetErrorMessage(
    payload: any,
    balanceAmount: number,
    maxWagerTotal: number
  ): string[] {
    const messages: string[] = [];

    if (payload?.dutchAbcErrorMsg) {
      messages.push(payload.dutchAbcErrorMsg);
    }

    if (payload?.cantBet) {
      messages.push('No valid wager possible');
    }

    const modifier = payload?.selectedModifier?.toLowerCase();

    if (modifier === 'dutchcost' && payload?.dutchTicket) {
      const dutchAmount = payload.dutchTicket.calcAmount();
      if (dutchAmount > balanceAmount) {
        messages.push(
          'Total $' + dutchAmount?.toFixed(2)
            + ' greater than current balance of $' + balanceAmount?.toFixed(2)
        );
      }
    } else if (modifier === 'abc' && payload?.abcTotalAmount) {
      if (payload.abcTotalAmount > balanceAmount) {
        messages.push(
          'Total $' + payload.abcTotalAmount?.toFixed(2)
            + ' greater than current balance of $' + balanceAmount?.toFixed(2)
        );
      }
    } else if (modifier !== 'abc' && modifier !== 'dutchcost') {
      const total = this.runnerService.betTotal(payload?.bets ?? 0, payload?.baseAmount ?? 0);
      const wagerMin = payload?.selectedPool?.WagerMinimum;

      if (total > maxWagerTotal) {
        messages.push(
          'Total $' + total?.toFixed(2)
            + ' over than Maximun total of $' + maxWagerTotal?.toFixed(2)
        );
      } else if (total > balanceAmount) {
        messages.push(
          'Total $' + total?.toFixed(2)
            + ' greater than current balance of $' + balanceAmount?.toFixed(2)
        );
      } else if (total < wagerMin) {
        // Wording kept in lock-step with mobile pending product clarification.
        const totalAmount = total > 0 ? total : 0;
        messages.push(
          'Amount $' + totalAmount?.toFixed(2)
            + ' less than minimum of $' + wagerMin?.toFixed(2)
        );
      }
    }

    return messages;
  }

  /**
   * Format the bet details summary line
   * @param {any} payload Show-bet payload
   * @returns {string} Formatted bet details
   */
  toDisplayBetDetails(
    payload: any,
    // selectedRace: any,
    // selectedPool: any,
    // selectedModifier: any
  ): string {

    let betDetails = '';

    if (payload?.selectedModifier?.toLowerCase() === 'dutchcost' && payload?.dutchTicket)
      betDetails = '$' + (payload?.dutchTicket?.calcAmount()?.toFixed(2));

    if (payload?.selectedModifier?.toLowerCase() === 'abc' && payload?.abcTotalAmount)
      betDetails = '$' +(payload?.abcTotalAmount?.toFixed(2));

    if (
      payload?.selectedModifier?.toLowerCase() !== 'abc' &&
      payload?.selectedModifier?.toLowerCase() !== 'dutchcost'
    )
      betDetails = '$' + (payload?.baseAmount?.toFixed(2));

    betDetails += ' ' + (
      payload?.selectedPool !== undefined ? payload?.selectedPool?.PoolId : ''
    );

    betDetails += ' ' + (
      payload?.selectedModifier !== undefined ?
      payload?.selectedModifier === 'DutchCost' ?
        'DUTCH' : payload?.selectedModifier : ''
    );

    betDetails += ' on '

    betDetails += payload?.storedSelectedRunners ? payload?.storedSelectedRunners : '';

    betDetails += ' ' + (payload?.dutchROI ? payload?.dutchROI : '');

    betDetails += ' ' + (
      payload?.selectedRace !== undefined ? 'R' + payload?.selectedRace?.RaceId : ''
    );

    return betDetails;
  }

  /**
   * Format the bet total amount respecting the max wager cap
   * @param {any} payload Show-bet payload
   * @param {number} maxWagerTotal Max wager total cap
   * @returns {string} Formatted total amount
   */
  toDisplayBetTotalAmount(
    payload: any,
    // selectedModifier: any,
    maxWagerTotal: number
  ): string {

    let totalAmount = 0;

    if (payload?.selectedModifier?.toLowerCase() === 'dutchcost' && payload?.dutchTicket)
      totalAmount = (
        payload?.dutchTicket.calcAmount() > 0 ? payload?.dutchTicket.calcAmount() : 0
      );

    if (payload?.selectedModifier?.toLowerCase() === 'abc' && payload?.abcTotalAmount)
      totalAmount = (payload?.abcTotalAmount > 0 ? payload?.abcTotalAmount : 0);

    if (
      payload?.selectedModifier?.toLowerCase() !== 'abc' &&
      payload?.selectedModifier?.toLowerCase() !== 'dutchcost'
    )
      totalAmount = this.runnerService.betTotal(
        payload?.bets ?? 0, payload?.baseAmount ?? 0
      ) > maxWagerTotal ?
          0
        :
          (
            this.runnerService.betTotal(payload?.bets ?? 0, payload?.baseAmount ?? 0) > 0 ?
                this.runnerService.betTotal(payload?.bets ?? 0, payload?.baseAmount ?? 0)
              :
                0
          );

    return totalAmount?.toFixed(2);
  }

  /**
   * Return the cleared processing-wager state
   * @returns {{setTimer: number, isProcessingBet: boolean}} Cleared state
   */
  clearProcessingWager(): {
    setTimer: number;
    isProcessingBet: boolean;
  } {
    const setTimer: number = 0;
    const isProcessingBet: boolean = false;

    return {
      setTimer,
      isProcessingBet
    }
  }

  /**
   * Clear in-flight bet timeout and return cleared processing state
   * @param {any} setTimer In-flight setTimeout handle, or null
   * @returns {{setTimer: number, isProcessingBet: boolean}} Cleared state
   */
  clearProcessingState(setTimer?: any): {
    setTimer: number;
    isProcessingBet: boolean;
  } {
    if (setTimer) {
      clearTimeout(setTimer);
    }
    return this.clearProcessingWager();
  }

  /**
   * Return the cleared bet-preview state (spread onto the component)
   * @returns {{importData: any, errorMessage: string[], betDetails: string, totalAmount: string}} Cleared bet-preview state
   */
  clearBetPreviewState(): {
    importData: any;
    errorMessage: string[];
    betDetails: string;
    totalAmount: string;
  } {
    return {
      importData: undefined,
      errorMessage: [],
      betDetails: '',
      totalAmount: '0.00'
    };
  }

  /**
   * Reset bet-preview fields plus processing guard on target
   * @param {BetPreviewResetTarget} target Bet-preview component
   * @returns {void}
   */
  resetPreview(target: BetPreviewResetTarget): void {
    const cleared = this.clearBetPreviewState();
    target.importData = cleared.importData;
    target.errorMessage = cleared.errorMessage;
    target.betDetails = cleared.betDetails;
    target.totalAmount = cleared.totalAmount;
    this.resetProcessingState(target);
  }

  /**
   * Reset the in-flight bet guard on target and mark for check
   * @param {BetPreviewResetTarget} target Bet-preview component
   * @returns {void}
   */
  resetProcessingState(target: BetPreviewResetTarget): void {
    const { setTimer, isProcessingBet } = this.clearProcessingState(target.setTimer);
    target.setTimer = setTimer;
    target.isProcessingBet = isProcessingBet;
    target.cdRef.markForCheck();
  }
}

/**
 * Structural shape accepted by resetPreview / resetProcessingState
 */
export interface BetPreviewResetTarget {
  importData: any;
  errorMessage: string[];
  betDetails: string;
  totalAmount: string;
  setTimer: any;
  isProcessingBet: boolean;
  cdRef: ChangeDetectorRef;
}
